import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Clock, Moon, Brain, Zap, Heart, ListMusic, Plus, Trash2, ChevronRight, Volume2 } from "lucide-react";
import { toast } from "sonner";
import GlassCard from "@/components/GlassCard";
import NeonButton from "@/components/NeonButton";
import CrystalIcon from "@/components/CrystalIcon";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

// Pre-built playlists
const builtInPlaylists = [
  {
    id: "sleep-journey",
    name: "Sleep Journey",
    description: "A 60-minute journey from relaxation to deep sleep",
    icon: Moon,
    color: "from-indigo-600 to-purple-600",
    duration: 60,
    phases: [
      { name: "Wind Down", duration: 15, frequency: 432, binaural: 10, noise: "pink", volume: 80 },
      { name: "Deep Relaxation", duration: 15, frequency: 396, binaural: 6, noise: "brown", volume: 60 },
      { name: "Pre-Sleep", duration: 15, frequency: 285, binaural: 4, noise: "brown", volume: 40 },
      { name: "Deep Sleep", duration: 15, frequency: 174, binaural: 2, noise: "brown", volume: 25 }
    ]
  },
  {
    id: "focus-flow",
    name: "Focus Flow",
    description: "90 minutes of enhanced concentration and productivity",
    icon: Brain,
    color: "from-cyan-600 to-blue-600",
    duration: 90,
    phases: [
      { name: "Warm Up", duration: 10, frequency: 432, binaural: 10, noise: "pink", volume: 70 },
      { name: "Light Focus", duration: 20, frequency: 528, binaural: 12, noise: "white", volume: 60 },
      { name: "Deep Focus", duration: 40, frequency: 528, binaural: 14, noise: "white", volume: 50 },
      { name: "Sustained", duration: 20, frequency: 639, binaural: 16, noise: "pink", volume: 55 }
    ]
  },
  {
    id: "energy-boost",
    name: "Energy Boost",
    description: "30-minute energizing session to wake up your mind",
    icon: Zap,
    color: "from-yellow-600 to-orange-600",
    duration: 30,
    phases: [
      { name: "Activation", duration: 10, frequency: 528, binaural: 14, noise: "white", volume: 70 },
      { name: "Peak Energy", duration: 15, frequency: 639, binaural: 20, noise: "blue", volume: 75 },
      { name: "Stabilize", duration: 5, frequency: 528, binaural: 16, noise: "pink", volume: 60 }
    ]
  },
  {
    id: "stress-relief",
    name: "Stress Relief",
    description: "45 minutes to release tension and find calm",
    icon: Heart,
    color: "from-pink-600 to-rose-600",
    duration: 45,
    phases: [
      { name: "Release", duration: 15, frequency: 396, binaural: 8, noise: "pink", volume: 70 },
      { name: "Calm", duration: 20, frequency: 432, binaural: 6, noise: "brown", volume: 55 },
      { name: "Peace", duration: 10, frequency: 528, binaural: 4, noise: "brown", volume: 45 }
    ]
  }
];

const Playlists = () => {
  const { isAuthenticated, isPremium } = useAuth();
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [volume, setVolume] = useState(70);
  const [customPlaylists, setCustomPlaylists] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load custom playlists from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('subspace_custom_playlists');
    if (saved) {
      setCustomPlaylists(JSON.parse(saved));
    }
  }, []);

  const handlePlayPlaylist = (playlist) => {
    if (!isPremium && !isAuthenticated) {
      toast.error("Sign in to access playlists");
      return;
    }
    setSelectedPlaylist(playlist);
    setCurrentPhaseIndex(0);
    setPhaseProgress(0);
    setIsPlaying(true);
    toast.success(`Starting ${playlist.name}`);
  };

  const handlePause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setSelectedPlaylist(null);
    setCurrentPhaseIndex(0);
    setPhaseProgress(0);
  };

  const handleSkipPhase = () => {
    if (selectedPlaylist && currentPhaseIndex < selectedPlaylist.phases.length - 1) {
      setCurrentPhaseIndex(prev => prev + 1);
      setPhaseProgress(0);
      toast.info(`Skipped to ${selectedPlaylist.phases[currentPhaseIndex + 1].name}`);
    } else {
      handleStop();
      toast.success("Playlist complete!");
    }
  };

  // Progress timer
  useEffect(() => {
    if (!isPlaying || !selectedPlaylist) return;

    const phase = selectedPlaylist.phases[currentPhaseIndex];
    const totalSeconds = phase.duration * 60;
    const increment = 100 / totalSeconds;

    const timer = setInterval(() => {
      setPhaseProgress(prev => {
        if (prev >= 100) {
          // Move to next phase
          if (currentPhaseIndex < selectedPlaylist.phases.length - 1) {
            setCurrentPhaseIndex(i => i + 1);
            return 0;
          } else {
            // Playlist complete
            handleStop();
            toast.success("Playlist complete!");
            return 100;
          }
        }
        return prev + increment;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPlaying, selectedPlaylist, currentPhaseIndex]);

  const currentPhase = selectedPlaylist?.phases[currentPhaseIndex];
  const totalDuration = selectedPlaylist?.phases.reduce((acc, p) => acc + p.duration, 0) || 0;
  const elapsedDuration = selectedPlaylist?.phases.slice(0, currentPhaseIndex).reduce((acc, p) => acc + p.duration, 0) || 0;
  const currentElapsed = currentPhase ? Math.floor((phaseProgress / 100) * currentPhase.duration) : 0;

  return (
    <div className="min-h-screen pt-20 md:pt-8 pb-32 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <p className="tagline-shimmer text-xs font-semibold uppercase mb-3" data-testid="playlists-tagline">
            Expand Your Mind.
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Sound <span className="text-neon">Playlists</span>
          </h1>
          <p className="text-gray-400">
            Curated sound journeys for sleep, focus, and relaxation
          </p>
        </motion.div>

        {/* Now Playing */}
        <AnimatePresence>
          {selectedPlaylist && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <GlassCard hover={false} className="border-violet-500/30">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                  {/* Playlist Info */}
                  <div className={cn(
                    "w-20 h-20 rounded-2xl bg-gradient-to-br flex items-center justify-center flex-shrink-0",
                    selectedPlaylist.color
                  )}>
                    <selectedPlaylist.icon size={32} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{selectedPlaylist.name}</h3>
                      <span className="text-xs px-2 py-1 rounded-full bg-violet-500/20 text-violet-400">
                        Now Playing
                      </span>
                    </div>
                    
                    {/* Current Phase */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-400">
                          Phase {currentPhaseIndex + 1}/{selectedPlaylist.phases.length}: <span className="text-white">{currentPhase?.name}</span>
                        </span>
                        <span className="text-xs text-gray-500">
                          {elapsedDuration + currentElapsed}m / {totalDuration}m
                        </span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className={cn("h-full bg-gradient-to-r", selectedPlaylist.color)}
                          style={{ width: `${((elapsedDuration + currentElapsed) / totalDuration) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Phase Progress */}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{currentPhase?.frequency}Hz</span>
                      <span>•</span>
                      <span>{currentPhase?.binaural}Hz binaural</span>
                      <span>•</span>
                      <span>{currentPhase?.noise} noise</span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Volume2 size={16} className="text-gray-500" />
                      <Slider
                        value={[volume]}
                        onValueChange={([v]) => setVolume(v)}
                        min={0}
                        max={100}
                        className="w-24"
                      />
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handlePause}
                      className={cn(
                        "w-14 h-14 rounded-full flex items-center justify-center",
                        isPlaying
                          ? "bg-gradient-to-r from-violet-600 to-indigo-600"
                          : "bg-white/10"
                      )}
                    >
                      {isPlaying ? <Pause size={24} className="text-white" /> : <Play size={24} className="text-white ml-1" />}
                    </motion.button>
                    
                    <NeonButton variant="ghost" size="sm" onClick={handleSkipPhase}>
                      Skip
                      <ChevronRight size={16} />
                    </NeonButton>
                    
                    <NeonButton variant="ghost" size="sm" onClick={handleStop}>
                      Stop
                    </NeonButton>
                  </div>
                </div>

                {/* Phase Timeline */}
                <div className="mt-6 flex gap-2">
                  {selectedPlaylist.phases.map((phase, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex-1 p-2 rounded-lg transition-all",
                        i === currentPhaseIndex
                          ? "bg-violet-500/20 border border-violet-500/30"
                          : i < currentPhaseIndex
                          ? "bg-green-500/10"
                          : "bg-white/5"
                      )}
                    >
                      <span className={cn(
                        "text-xs font-medium block",
                        i === currentPhaseIndex ? "text-violet-400" : i < currentPhaseIndex ? "text-green-400" : "text-gray-500"
                      )}>
                        {phase.name}
                      </span>
                      <span className="text-[10px] text-gray-600">{phase.duration}m</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Built-in Playlists */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <ListMusic size={20} className="text-violet-400" />
            Featured Playlists
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {builtInPlaylists.map((playlist, i) => (
              <motion.div
                key={playlist.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div
                  className={cn(
                    "crystal-card group cursor-pointer transition-all relative",
                    selectedPlaylist?.id === playlist.id && "crystal-card-glow"
                  )}
                  onClick={() => !isPlaying && handlePlayPlaylist(playlist)}
                  data-testid={`playlist-card-${playlist.id}`}
                >
                  {/* Play button — top right corner */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlayPlaylist(playlist);
                    }}
                    data-testid={`playlist-play-${playlist.id}`}
                    className={cn(
                      "absolute top-4 right-4 w-11 h-11 rounded-full flex items-center justify-center z-10 transition-colors",
                      selectedPlaylist?.id === playlist.id && isPlaying
                        ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-[0_0_20px_rgba(217,70,239,0.5)]"
                        : "bg-white/10 hover:bg-white/20 border border-white/10"
                    )}
                  >
                    {selectedPlaylist?.id === playlist.id && isPlaying ? (
                      <Pause size={18} className="text-white" />
                    ) : (
                      <Play size={18} className="text-white ml-0.5" />
                    )}
                  </motion.button>

                  {/* Centered crystal icon */}
                  <CrystalIcon icon={playlist.icon} accent={`pl${i}`} size={64} />

                  {/* Centered title + description */}
                  <h3 className="text-lg font-semibold text-white mb-2 text-center">
                    {playlist.name}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed text-center mb-4">
                    {playlist.description}
                  </p>

                  {/* Meta row centered */}
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {playlist.duration}m
                    </span>
                    <span className="text-gray-700">•</span>
                    <span>{playlist.phases.length} phases</span>
                  </div>

                  {/* Phase preview bar */}
                  <div className="flex gap-1">
                    {playlist.phases.map((phase, j) => (
                      <div
                        key={j}
                        className="h-1 rounded-full bg-white/15 flex-1"
                        style={{ flex: phase.duration }}
                        title={`${phase.name} (${phase.duration}m)`}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Custom Playlists */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Plus size={20} className="text-cyan-400" />
              My Playlists
            </h2>
            
            {isPremium && (
              <NeonButton size="sm" onClick={() => setShowCreateModal(true)}>
                <Plus size={16} className="mr-1" />
                Create Playlist
              </NeonButton>
            )}
          </div>

          {customPlaylists.length === 0 ? (
            <GlassCard hover={false} className="text-center py-12">
              <ListMusic size={48} className="mx-auto text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No custom playlists yet</h3>
              <p className="text-sm text-gray-500 mb-4">
                {isPremium 
                  ? "Create your own sound journey by combining phases"
                  : "Upgrade to Pro to create custom playlists"
                }
              </p>
              {!isPremium && (
                <NeonButton size="sm" onClick={() => window.location.href = '/pricing'}>
                  Upgrade to Pro
                </NeonButton>
              )}
            </GlassCard>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {customPlaylists.map((playlist, i) => (
                <GlassCard key={playlist.id} className="cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                      <ListMusic size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-white">{playlist.name}</h3>
                      <span className="text-xs text-gray-500">{playlist.phases?.length || 0} phases</span>
                    </div>
                    <button
                      onClick={() => {
                        const updated = customPlaylists.filter(p => p.id !== playlist.id);
                        setCustomPlaylists(updated);
                        localStorage.setItem('subspace_custom_playlists', JSON.stringify(updated));
                        toast.success("Playlist deleted");
                      }}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>

        {/* Info Card */}
        <GlassCard hover={false} className="mt-12">
          <h3 className="text-lg font-semibold text-white mb-3">How Sound Playlists Work</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
            <div>
              <h4 className="text-white font-medium mb-1">Phased Progression</h4>
              <p>Each playlist guides you through multiple phases, gradually adjusting frequencies and sounds.</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">Brainwave Entrainment</h4>
              <p>Binaural beats and frequencies are calibrated to guide your brain into specific states.</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-1">Automatic Transitions</h4>
              <p>Smooth crossfades between phases ensure a seamless, uninterrupted experience.</p>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default Playlists;
